<template>
  <div ref="root" class="sp-hero-item" :class="classModifiers">
    <div class="sp-hero-item__background">
      <div v-if="video" class="sp-hero-item__background-asset">
        <video ref="videoRef" :src="video" :aria-label="title" :autoplay="active" muted loop playsinline />
      </div>

      <div v-else class="sp-hero-item__background-asset">
        <slot name="image">
          <img :src="image" :alt="title" />
        </slot>
      </div>
      <div class="sp-hero-item__background-overlay"></div>
    </div>

    <div class="sp-hero-item__content">
      <sp-text v-if="title" tag="h2" :type="titleSize" class="sp-hero-item__title">
        {{ title }}
      </sp-text>

      <div class="sp-hero-item__content-body">
        <slot />
      </div>

      <sp-hero-action v-if="actionUrl" v-bind="actionProps">{{ actionLabel }}</sp-hero-action>

      <div class="sp-hero-item__captions">
        <slot name="captions">{{ captions }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { useExpose } from "../../composables/expose";
import { toBoolean } from "../../utils/props";

const props = defineProps({
  titleSize: { type: String, default: "h1" },
  title: { type: String, default: undefined },
  image: { type: String, default: undefined },
  video: { type: String, default: undefined },
  captions: { type: String, default: undefined },
  active: { type: [Boolean, String], default: false },
  align: { type: String, default: "start", validator: (value) => ["start", "center", "end"].includes(value) },
  theme: { type: String, default: "light", validator: (value) => ["dark", "light"].includes(value) },
  // Action
  actionUrl: { type: String, default: undefined },
  actionLabel: { type: String, default: undefined },
  actionColor: { type: String, default: undefined },
  actionVariant: { type: String, default: undefined },
});

const root = ref(null);
const active = ref(toBoolean(props.active));
const videoRef = ref(null);
const actionProps = computed(() => ({
  url: props.actionUrl,
  color: props.actionColor,
  variant: props.actionVariant,
  label: props.actionLabel,
}));

const { isSmScreen, isMdScreen, isLgScreen } = useBreakpoints(root);
const { exposeMethods } = useExpose(root);

watch(active, (isActive) => {
  if (isActive && videoRef.value) {
    videoRef.value.currentTime = 0;
  }
});

const classModifiers = computed(() => ({
  "--has-title": props.title !== undefined,
  "--has-image": props.image !== undefined,
  "--has-video": props.video !== undefined,
  "--active": active.value,
  "--is-small-screen": isSmScreen.value,
  "--is-medium-screen": isMdScreen.value,
  "--is-large-screen": isLgScreen.value,
  [`--theme-${props.theme}`]: props.theme,
  [`--align-${props.align}`]: props.align,
}));

exposeMethods({
  setActive(isActive) {
    active.value = toBoolean(isActive);
  },
});
</script>

<style>
:host {
  display: block;
  height: 100%;
  width: 100%;
  position: absolute;
}
</style>

<style scoped lang="scss">
.sp-hero-item {
  --item-font: var(--sp-comp-hero-item-font, var(--sp-sys-typescale-body-short));
  --item-title-font: var(--sp-comp-hero-item-title-font, var(--sp-sys-typescale-h1-short));
  --item-title-offset: var(--sp-comp-hero-item-title-offset, calc(var(--sp-ref-spacing-8) * -1));

  --item-content-font: var(--sp-comp-hero-item-content-font, var(--item-font));
  --item-content-line-height: var(--sp-comp-hero-item-content-line-height, var(--sp-sys-typescale-body-line-height));
  --item-content-gap: var(--sp-comp-hero-item-content-gap, 1rem);
  --item-overlay-dark-background-color: var(
    --sp-comp-hero-item-overlay-dark-start-background-color,
    var(--sp-sys-color-black, #000)
  );
  --item-overlay-light-background-color: var(
    --sp-comp-hero-item-overlay-light-start-background-color,
    var(--sp-sys-color-white, #fff)
  );

  --item-content-max-width: var(--sp-comp-hero-item-content-max-width, 40%);
  --item-content-body-max-width: var(--sp-comp-hero-item-content-body-max-width, 80%);

  display: flex;
  font: var(--item-font);
  justify-content: v-bind(align);
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity var(--transition-duration, 500ms) ease;

  &.--active {
    opacity: 1;
  }

  &.--align-start {
    --item-overlay-direction: to right;
  }

  &.--align-center {
    --item-overlay-direction: to bottom;
  }

  &.--align-end {
    --item-overlay-direction: to left;
  }

  &.--is-small-screen {
    --item-overlay-direction: to bottom;
    --item-content-max-width: 100%;
    --item-content-body-max-width: 100%;
    --item-padding-inline: var(--item-padding-inline-sm);
    --item-padding-block: var(--item-padding-block-sm);
    --item-content-gap: var(--sp-ref-spacing-8);
    --item-title-font: var(--sp-comp-hero-item-title-font-sm, var(--sp-sys-typescale-h1-small-short));
    --item-title-offset: var(--sp-comp-hero-item-title-offset-sm, calc(var(--sp-ref-spacing-4) * -1));
    --item-content-font: var(--sp-comp-hero-item-content-font-sm, var(--sp-sys-typescale-body-small-short));
    --item-content-line-height: var(
      --sp-comp-hero-item-content-line-height-sm,
      var(--sp-sys-typescale-body-small-line-height)
    );
  }

  &.--is-large-screen,
  &.--is-medium-screen {
    --item-title-font: var(--sp-comp-hero-item-title-font-md, var(--sp-sys-typescale-h1-short));
    --item-padding-inline: var(--item-padding-inline-md);

    --item-padding-block-md-start: var(--sp-comp-hero-item-padding-block-md-start, var(--sp-ref-spacing-32));
    --item-padding-block-md-end: calc(
      var(--item-padding-slideshow-nav-offset) + var(--sp-comp-hero-item-padding-block-md-end, var(--sp-ref-spacing-40))
    );
    --item-padding-block-md: var(--item-padding-block-md-start) var(--item-padding-block-md-end);
    --item-padding-block: var(--item-padding-block-md);
  }

  &.--theme-dark {
    --item-overlay-start-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-start-background-color,
      var(--item-overlay-dark-background-color)
    );

    --item-overlay-end-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-end-background-color,
      var(--item-overlay-light-background-color)
    );

    --item-overlay-start-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-start-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 70%)
    );

    --item-overlay-end-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-end-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 10%)
    );

    --item-color: var(--sp-comp-hero-item-theme-dark-text-color, var(--sp-sys-color-on-black, #fff));
  }

  &.--theme-light {
    --item-overlay-start-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-start-background-color,
      var(--item-overlay-light-background-color)
    );

    --item-overlay-end-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-end-background-color,
      var(--item-overlay-dark-background-color)
    );

    --item-overlay-start-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-start-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 70%)
    );

    --item-overlay-end-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-end-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 10%)
    );

    --item-color: var(--sp-comp-hero-item-theme-light-text-color, var(--sp-sys-color-on-white, #000));
  }

  ::slotted(sp-responsive-image) {
    --sp-responsive-image-border-radius: var(--item-border-radius);
  }

  &__title {
    font: var(--item-title-font);
    margin-block: 0;
    margin-block-end: var(--item-title-offset);
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: var(--item-border-radius);
    width: var(--item-background-width);
    margin-inline: var(--item-background-margin-inline);

    &-overlay {
      --item-overlay-start-gradient: color-mix(
        in srgb,
        var(--item-overlay-start-background-color) var(--item-overlay-start-alpha),
        transparent
      );
      --item-overlay-end-gradient: color-mix(
        in srgb,
        var(--item-overlay-end-background-color) var(--item-overlay-end-alpha),
        transparent
      );

      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      border-radius: var(--item-border-radius);
      background: linear-gradient(
        var(--item-overlay-direction),
        var(--item-overlay-start-gradient),
        var(--item-overlay-end-gradient)
      );
    }

    &-asset {
      width: 100%;
      height: 100%;
    }

    video,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    --sp-comp-layout-row-color-link: var(--item-color);
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: var(--item-content-max-width);
    gap: var(--item-content-gap);
    z-index: 2;
    padding-block: var(--item-padding-block);
    padding-inline: var(--item-padding-inline);
    color: var(--item-color);
    text-shadow: 0 var(--sp-ref-spacing-1) var(--sp-ref-spacing-2) rgba(0, 0, 0, 0.5);

    &-body {
      --sp-comp-wysiwyg-content-font: var(--item-content-font);
      --sp-comp-wysiwyg-content-line-height: var(--item-content-line-height);
      display: flex;
      max-width: var(--item-content-body-max-width);
      flex-direction: column;
      align-self: v-bind(align);
      font: var(--item-content-font);
    }

    .sp-hero-item__captions {
      flex-grow: 1;
      display: flex;
      align-items: flex-end;
    }
  }
}
</style>
